/**
 * Consumable Registry
 *
 * The per-kind registry for consumables, in the same shape as
 * `weaponRegistry` and `abilityRegistry`: it holds instances and nothing more.
 * The environment populates it (server at boot from `config/consumables.json`,
 * client from the same file over HTTP) and `itemRegistry` routes refs to it.
 */

import { Consumable } from './Consumable.js';
import { getSharedLogger } from './utils/loggerProxy.js';

const logger = getSharedLogger('GENERAL');

class ConsumableRegistry {
  constructor() {
    /** @type {Map<string, Consumable>} */
    this.consumables = new Map();
  }

  /**
   * @param {Consumable|Object} consumableOrConfig a plain config is wrapped
   * @returns {Consumable|null}
   */
  register(consumableOrConfig) {
    if (!consumableOrConfig?.id) {
      logger.warn('[consumableRegistry] Refusing a consumable with no id');
      return null;
    }

    const consumable = consumableOrConfig instanceof Consumable
      ? consumableOrConfig
      : new Consumable(consumableOrConfig);

    this.consumables.set(consumable.id, consumable);
    return consumable;
  }

  /**
   * @param {Array<Object>} configs
   * @returns {number} how many registered
   */
  registerBulk(configs) {
    if (!Array.isArray(configs)) return 0;
    return configs.reduce((count, config) => count + (this.register(config) ? 1 : 0), 0);
  }

  /**
   * @param {string} consumableId
   * @returns {Consumable|null}
   */
  get(consumableId) {
    return this.consumables.get(consumableId) || null;
  }

  /**
   * @param {string} consumableId
   * @returns {boolean}
   */
  has(consumableId) {
    return this.consumables.has(consumableId);
  }

  /** @returns {Consumable[]} */
  getAll() {
    return Array.from(this.consumables.values());
  }

  /** @returns {string[]} */
  getAllIds() {
    return Array.from(this.consumables.keys());
  }
}

// Populated by the environment, never by this file: the server at boot
// (src/server/runtime/registries.js), the client from its config fetch.
const consumableRegistry = new ConsumableRegistry();

export { ConsumableRegistry, consumableRegistry };
